{
  "nav": {
    "overview": "Overview",
    "budgets": "Budgets"
  },
  "overview": {
    "title": "Studio",
    "description": "Build agents, connect models and tools, and keep an eye on what they do.",
    "setup": {
      "title": "Get started",
      "description": "Three steps to your first working agent.",
      "provider": "Connect a model provider",
      "model": "Add a model",
      "agent": "Create your first agent"
    },
    "stats": {
      "agents": "Active agents",
      "archived_one": "{{count}} archived",
      "archived_other": "{{count}} archived",
      "runs7d": "Runs (7 days)",
      "models": "Models",
      "providers": "Providers",
      "integrations": "Integrations",
      "skills": "Skills"
    },
    "recentRuns": "Recent runs",
    "noRuns": "No runs yet. Try an agent to see its first run here.",
    "viewAll": "View all",
    "budgets": "Budget status",
    "noBudgets": "No budget policies",
    "noBudgetsHint": "Cap spend per organization or per agent.",
    "manage": "Manage",
    "quickLinks": "Jump to",
    "links": {
      "agents": "Instructions, models, tools and skills per agent.",
      "skills": "Reusable instructions agents load on demand.",
      "mcp": "Connect tools over MCP, with OAuth or headers.",
      "models": "Models, limits and prices.",
      "providers": "API endpoints and keys for model vendors.",
      "runs": "Every run with prompt, tools and cost."
    }
  },
  "tags": {
    "title": "Tags",
    "manage": "Manage tags",
    "manageDescription": "Tags group agents and filter the agent list. Rename or recolour them here.",
    "searchOrCreate": "Search or create a tag…",
    "create": "Create “{{name}}”",
    "none": "No tags yet",
    "empty": "No tags yet",
    "add": "Add",
    "name": "Tag name",
    "newPlaceholder": "New tag, e.g. Support",
    "customColor": "Custom colour",
    "remove": "Remove tag {{name}}",
    "delete": "Delete tag",
    "deleteTitle": "Delete tag “{{name}}”?",
    "deleteDescription_one": "It is removed from {{count}} agent. The agent itself stays.",
    "deleteDescription_other": "It is removed from {{count}} agents. The agents themselves stay.",
    "deleted": "Tag “{{name}}” deleted",
    "agentCount_one": "{{count}} agent",
    "agentCount_other": "{{count}} agents"
  },
  "kv": {
    "key": "Name",
    "value": "Value",
    "add": "Add",
    "remove": "Remove",
    "duplicate": "Names must be unique."
  },
  "markdown": {
    "mode": "Editor mode",
    "write": "Write",
    "preview": "Preview",
    "split": "Side by side",
    "nothing": "Nothing to preview yet.",
    "stats": "{{chars}} characters · ~{{tokens}} tokens"
  },
  "diff": {
    "identical": "No differences.",
    "lines": "lines changed",
    "unchanged_one": "{{count}} unchanged line",
    "unchanged_other": "{{count}} unchanged lines",
    "added": "added:",
    "removed": "removed:",
    "field": "Setting",
    "before": "Before",
    "after": "After"
  },
  "agents": {
    "title": "Agents",
    "description": "Configure what each agent knows, which model it runs on and which tools it may use.",
    "editorTitle": "Agent",
    "backToList": "All agents",
    "new": "New agent",
    "newTitle": "New agent",
    "newDescription": "Give it a name and a model. You can configure everything else next.",
    "createAndConfigure": "Create and configure",
    "defaultPrompt": "You are {{name}}, a helpful assistant. Answer clearly and concisely.",
    "search": "Search agents…",
    "filterTag": "Filter by tag",
    "allTags": "All tags",
    "showArchived": "Show archived",
    "clearFilters": "Clear filters",
    "emptyFiltered": "No agents match these filters",
    "empty": {
      "title": "No agents yet",
      "description": "Create your first agent to configure its instructions, model, tools and skills.",
      "action": "Create your first agent"
    },
    "noModels": {
      "title": "Add a model first",
      "description": "Agents run on a model. Connect a provider and register at least one model.",
      "action": "Go to models"
    },
    "columns": {
      "agent": "Agent",
      "model": "Model",
      "tags": "Tags",
      "capabilities": "Tools & skills",
      "runs7d": "Runs (7d)",
      "lastRun": "Last run"
    },
    "integrationCount_one": "{{count}} integration",
    "integrationCount_other": "{{count}} integrations",
    "skillCount_one": "{{count}} skill",
    "skillCount_other": "{{count}} skills",
    "chatEnabled": "Available in chat",
    "archivedBadge": "Archived",
    "noDescription": "No description",
    "neverRun": "Never",
    "moreActions": "More actions",
    "actions": {
      "edit": "Edit",
      "try": "Try it",
      "duplicate": "Duplicate",
      "archive": "Archive",
      "unarchive": "Restore from archive",
      "delete": "Delete",
      "viewRuns": "View runs"
    },
    "created": "Agent “{{name}}” created",
    "saved": "“{{name}}” saved",
    "duplicated": "Created “{{name}}”",
    "archived": "“{{name}}” archived",
    "unarchived": "“{{name}}” restored",
    "deleted": "“{{name}}” deleted",
    "archive": {
      "title": "Archive “{{name}}”?",
      "description": "Archived agents disappear from chat and can no longer be run. Their history stays and you can restore them at any time.",
      "confirm": "Archive"
    },
    "delete": {
      "title": "Delete “{{name}}”?",
      "description": "The agent, its revisions and its tool assignments are removed. Past runs stay in the run log.",
      "confirm": "Delete agent",
      "blockedTitle": "“{{name}}” can't be deleted",
      "archiveInstead": "Archive instead"
    },
    "leave": {
      "title": "Leave without saving?",
      "description": "Your changes to this agent will be lost.",
      "confirm": "Discard changes",
      "stay": "Keep editing"
    },
    "tabs": {
      "configure": "Configure",
      "tools": "Tools",
      "skills": "Skills",
      "revisions": "Revisions"
    },
    "icon": {
      "change": "Change icon",
      "title": "Icon",
      "custom": "Any emoji…",
      "use": "Use",
      "initials": "Use initials"
    },
    "sections": {
      "identity": {
        "title": "Identity",
        "description": "How people find and recognise this agent."
      },
      "instructions": {
        "title": "Instructions",
        "description": "The system prompt: role, tone, rules and the knowledge the agent always needs."
      },
      "model": {
        "title": "Model & sampling",
        "description": "Which model answers, and how deterministic its answers are."
      },
      "reasoning": {
        "title": "Reasoning",
        "description": "Let the model think before it answers. Slower and more expensive, but better at hard problems."
      },
      "behaviour": {
        "title": "Behaviour",
        "description": "Where the agent is available, how long it may work and how its prompt is cached."
      },
      "nav": "Sections"
    },
    "fields": {
      "name": "Name",
      "description": "Description",
      "descriptionHint": "Shown in the chat agent picker and used by other agents to decide when to delegate.",
      "descriptionPlaceholder": "What this agent is good at…",
      "tags": "Tags",
      "addTag": "Add tag",
      "model": "Model",
      "temperature": "Temperature",
      "temperatureHint": "{{hint}} Lower is more focused and repeatable, higher is more varied.",
      "temperatureFocused": "Focused.",
      "temperatureBalanced": "Balanced.",
      "temperatureCreative": "Creative.",
      "temperatureWild": "Very random.",
      "topP": "Top P",
      "topPHint": "Nucleus sampling, 0–1.",
      "topK": "Top K",
      "topKHint": "Sample from the K most likely tokens.",
      "topKUnsupported": "Not supported by OpenAI models.",
      "maxTokens": "Max output",
      "maxTokensHint": "Upper limit per answer.",
      "tokensUnit": "tokens",
      "default": "Default",
      "thinking": "Extended thinking",
      "thinkingHint": "The model reasons step by step before answering. Its reasoning shows in chat and in the run log.",
      "effort": "Reasoning effort",
      "effortHint": "How much the model thinks before answering.",
      "effortDisabled": "Turn on extended thinking to choose an effort.",
      "chatAgent": "Available in chat",
      "chatAgentHint": "Members can pick this agent in Chat (subject to their chat agent permissions).",
      "maxSteps": "Max steps",
      "maxStepsHint": "Model calls per run, including tool use.",
      "stepsUnit": "steps",
      "cacheStatic": "Cache instructions & tools",
      "cacheStaticHint": "How long the system prompt and tool list stay cached.",
      "cacheMessages": "Cache conversation",
      "cacheMessagesHint": "How long earlier messages stay cached.",
      "cacheAnthropicOnly": "Prompt caching settings are only used by Anthropic models."
    },
    "effort": {
      "low": "Low",
      "medium": "Medium",
      "high": "High",
      "xhigh": "Extra high",
      "max": "Maximum"
    },
    "cache": {
      "5m": "5 minutes",
      "1h": "1 hour"
    },
    "model": {
      "manage": "Manage models",
      "context": "{{n}} context",
      "price": "{{input}} in / {{output}} out per 1M",
      "fallback": "Falls back to {{name}}"
    },
    "prompt": {
      "placeholder": "You are a helpful assistant for…",
      "stats_one": "{{lines}} line · {{chars}} characters · ~{{tokens}} tokens",
      "stats_other": "{{lines}} lines · {{chars}} characters · ~{{tokens}} tokens",
      "expand": "Expand editor",
      "collapse": "Shrink editor"
    },
    "save": {
      "note": "Revision note",
      "notePlaceholder": "What changed? (optional, saved with the revision)",
      "invalid": "Fix the highlighted fields to save.",
      "fields": {
        "name": "Name",
        "description": "Description",
        "icon": "Icon",
        "model_id": "Model",
        "system_prompt": "Instructions",
        "temperature": "Temperature",
        "top_p": "Top P",
        "top_k": "Top K",
        "max_tokens": "Max output",
        "thinking_enabled": "Thinking",
        "reasoning_effort": "Reasoning effort",
        "prompt_cache_static": "Instruction cache",
        "prompt_cache_messages": "Conversation cache",
        "max_steps": "Max steps",
        "allow_as_chat_agent": "Chat availability",
        "tag_ids": "Tags",
        "skill_ids": "Skills",
        "mcp_servers": "Integrations"
      }
    },
    "tools": {
      "title": "Integrations",
      "description": "Choose which MCP integrations and built-in toolsets this agent may use, and switch off individual tools.",
      "searchIntegrations": "Search integrations…",
      "manage": "Manage integrations",
      "assign": "Use {{name}}",
      "configure": "Tools",
      "all": "All tools",
      "enabledCount": "{{enabled}} of {{total}} enabled",
      "search": "Search tools…",
      "listLabel": "Tools of {{name}}",
      "notAllowed": "Off for the organization",
      "orgLimited_one": "{{count}} tool is turned off for the whole organization in the integration settings.",
      "orgLimited_other": "{{count}} tools are turned off for the whole organization in the integration settings.",
      "someDisabled_one": "{{count}} tool off",
      "someDisabled_other": "{{count}} tools off",
      "noTools": "This integration exposes no tools.",
      "loadFailed": "Couldn't load the tools",
      "orphaned_one": "{{count}} assigned integration no longer exists.",
      "orphaned_other": "{{count}} assigned integrations no longer exist.",
      "removeOrphaned": "Remove",
      "empty": {
        "title": "No integrations yet",
        "description": "Connect an MCP server to give agents tools such as search, tickets or calendars.",
        "action": "Add an integration"
      }
    },
    "skills": {
      "title": "Skills",
      "description": "Skills are instructions the agent loads only when it needs them, which keeps the prompt small.",
      "applyPreset": "Add a preset…",
      "manage": "Manage skills",
      "search": "Search skills…",
      "selectedCount_one": "{{count}} selected",
      "selectedCount_other": "{{count}} selected",
      "previewEmpty": "Select a skill to preview it",
      "empty": {
        "title": "No skills yet",
        "description": "Write or import skills in the skill library, then assign them here.",
        "action": "Open the skill library"
      }
    },
    "revisions": {
      "hint": "A revision is saved whenever the model, instructions or sampling change.",
      "current": "Current",
      "untitled": "Revision #{{id}}",
      "savedBy": "{{date}} by {{user}}",
      "system": "the system",
      "compareWith": "Compare with",
      "vsPrevious": "vs. previous",
      "vsCurrent": "vs. current",
      "restore": "Restore",
      "restoreTitle": "Restore this revision?",
      "restoreDescription": "The agent goes back to the model, instructions and sampling from {{date}}. A new revision is created, so nothing is lost.",
      "restoreDiscards": "Your unsaved changes will be discarded.",
      "restored": "Revision restored",
      "first": "This is the first revision.",
      "settings": "Settings",
      "noSettingChanges": "No setting changes.",
      "prompt": "Instructions",
      "fields": {
        "model": "Model",
        "temperature": "Temperature",
        "top_p": "Top P",
        "top_k": "Top K",
        "max_tokens": "Max output",
        "thinking_enabled": "Thinking",
        "reasoning_effort": "Reasoning effort",
        "max_steps": "Max steps",
        "description": "Description"
      },
      "empty": {
        "title": "No revisions yet",
        "description": "Revisions appear when the model, instructions or sampling change."
      }
    },
    "try": {
      "button": "Try it",
      "title": "Try it",
      "description": "Chat with this agent using the settings in the editor, saved or not. Test conversations don't appear in chat history.",
      "emptyTitle": "Talk to {{name}}",
      "placeholder": "Message the agent…",
      "send": "Send",
      "stop": "Stop",
      "reset": "New conversation",
      "close": "Close",
      "thinking": "Thinking…",
      "reasoning": "Reasoning",
      "working": "Working…",
      "failed": "The run failed.",
      "cancelled": "Stopped.",
      "fixErrors": "Fix the errors in the editor first.",
      "openRun": "Open run",
      "tokens": "{{n}} tokens",
      "unsaved": "Unsaved settings",
      "unsavedHint": "Replies use the instructions, model, sampling, thinking, cache, skills and integrations from the editor, even before you save.",
      "ignored": "Reasoning effort, output limit, max steps and per-tool switches apply after saving.",
      "starters": {
        "intro": "Introduce yourself in two sentences.",
        "capabilities": "What can you help me with?",
        "tools": "Which tools do you have access to?"
      }
    }
  },
  "providers": {
    "title": "Providers",
    "description": "Endpoints and API keys for the model vendors your agents use. Keys are encrypted and never shown again.",
    "new": "Add provider",
    "newTitle": "Add a model provider",
    "editTitle": "Edit {{name}}",
    "formDescription": "Anthropic, OpenAI, any OpenAI-compatible gateway (vLLM, LiteLLM, Azure…) or Ollama.",
    "create": "Add provider",
    "search": "Search providers…",
    "defaultEndpoint": "Default endpoint",
    "noKey": "No key",
    "created": "Provider “{{name}}” added",
    "updated": "Provider “{{name}}” saved",
    "deleted": "Provider “{{name}}” deleted",
    "delete": "Delete provider",
    "deleteTitle": "Delete “{{name}}”?",
    "deleteDescription": "The stored API key and headers are removed.",
    "deleteInUse_one": "{{count}} model still uses this provider. Delete or move it first.",
    "deleteInUse_other": "{{count}} models still use this provider. Delete or move them first.",
    "empty": {
      "title": "No providers yet",
      "description": "Connect Anthropic, OpenAI, an OpenAI-compatible endpoint or Ollama to start adding models."
    },
    "columns": {
      "name": "Provider",
      "kind": "Kind",
      "apiKey": "API key",
      "models": "Models",
      "status": "Connection",
      "updated": "Updated"
    },
    "kinds": {
      "openai_compatible": "OpenAI-compatible"
    },
    "kindHints": {
      "anthropic": "Claude models via the Anthropic API.",
      "openai": "GPT models via the OpenAI API.",
      "openai_compatible": "vLLM, LiteLLM, Azure OpenAI, OpenRouter and other gateways.",
      "ollama": "Local models served by Ollama."
    },
    "form": {
      "name": "Name",
      "kind": "Kind",
      "baseUrl": "Base URL",
      "baseUrlHint": "Leave empty for the vendor's default endpoint.",
      "baseUrlHintCompat": "Usually ends in /v1. From Docker, use host.docker.internal instead of localhost.",
      "baseUrlRequired": "A base URL is required for this kind of provider.",
      "apiKey": "API key",
      "apiKeyHint": "Stored encrypted. Only the last characters are shown afterwards.",
      "apiKeyStored": "A key is stored. Enter a new one to replace it.",
      "apiKeyKeep": "Keep current key ({{hint}})",
      "apiKeyOptional": "Optional",
      "clearKey": "Remove the stored key",
      "strict": "Strict OpenAI compatibility",
      "strictHint": "Send only the most widely supported parameters (recommended for Ollama and older gateways).",
      "headers": "Custom headers",
      "headersHint": "Sent with every request, e.g. an API version or organization header. Values are write-only.",
      "noHeaders": "No custom headers",
      "replaceHeaders": "Replace headers",
      "addHeader": "Add header"
    },
    "test": {
      "action": "Test connection",
      "title": "Connection",
      "hint": "Checks the endpoint and key by listing the available models.",
      "running": "Connecting…",
      "ok_one": "{{name}} answered in {{ms}} ms with {{count}} model.",
      "ok_other": "{{name}} answered in {{ms}} ms with {{count}} models.",
      "failed": "Couldn't reach {{name}}",
      "online": "Online · {{ms}} ms",
      "offline": "Unreachable"
    },
    "import": {
      "action": "Import models",
      "title": "Import models from {{name}}",
      "description": "Pick the models you want to use. Models you already registered are marked.",
      "search": "Filter models…",
      "summary": "{{total}} found · {{fresh}} new",
      "selectAll": "Select all new models",
      "added": "Added",
      "empty": "This provider reports no models.",
      "failed": "Couldn't list the provider's models",
      "submit_one": "Import {{count}} model",
      "submit_other": "Import {{count}} models",
      "done_one": "{{count}} model imported",
      "done_other": "{{count}} models imported"
    }
  },
  "models": {
    "title": "Models",
    "description": "The models agents can run on, with their limits, fallbacks and prices for cost tracking.",
    "new": "Add model",
    "newTitle": "Add a model",
    "editTitle": "Edit {{name}}",
    "formDescription": "Register a model offered by one of your providers.",
    "create": "Add model",
    "search": "Search models…",
    "importFrom": "Import",
    "chooseProvider": "From provider",
    "allProviders": "All providers",
    "vision": "Vision",
    "tools": "Tool use",
    "noPrice": "No price",
    "limits": "{{ctx}} ctx · {{output}} out",
    "fallbackShort": "→ {{name}}",
    "created": "Model “{{name}}” added",
    "updated": "Model “{{name}}” saved",
    "deleted": "Model “{{name}}” deleted",
    "delete": "Delete model",
    "deleteTitle": "Delete “{{name}}”?",
    "deleteDescription": "Its price history is removed too.",
    "deleteInUse_one": "{{count}} agent runs on this model. Switch it to another model first.",
    "deleteInUse_other": "{{count}} agents run on this model. Switch them to another model first.",
    "noProviders": {
      "title": "Connect a provider first",
      "description": "Models belong to a provider. Add Anthropic, OpenAI, an OpenAI-compatible endpoint or Ollama."
    },
    "empty": {
      "title": "No models yet",
      "description": "Add a model by hand or import several at once from a provider."
    },
    "columns": {
      "model": "Model",
      "provider": "Provider",
      "limits": "Limits",
      "capabilities": "Capabilities",
      "price": "Price per 1M (in / out)",
      "agents": "Agents"
    },
    "form": {
      "provider": "Provider",
      "model": "Model ID",
      "modelHint": "Exactly as the provider's API expects it.",
      "name": "Display name",
      "nameHint": "Defaults to the model ID.",
      "description": "Description",
      "fallback": "Fallback model",
      "fallbackHint": "Used automatically when this model is unavailable or overloaded.",
      "noFallback": "No fallback",
      "context": "Context window",
      "maxOutput": "Max output",
      "visionHint": "Accepts images and PDFs.",
      "toolsHint": "Can call tools (MCP integrations, skills)."
    },
    "prices": {
      "title": "Pricing",
      "description": "Each price applies from its date on. Past runs keep the price that was valid then.",
      "initialHint": "Optional. Used to calculate the cost of runs and budgets.",
      "add": "Add price",
      "newTitle": "New price",
      "editTitle": "Edit price",
      "dialogDescription": "Prices are per million tokens. A new effective date keeps the price history intact.",
      "empty": "No prices yet. Runs on this model show no cost.",
      "current": "Current",
      "scheduled": "Scheduled",
      "input": "Input price",
      "output": "Output price",
      "inputShort": "Input",
      "outputShort": "Output",
      "cacheRead": "Cache read",
      "cacheWrite": "Cache write",
      "cacheShort": "Cache r/w",
      "per1m": "/ 1M",
      "currency": "Currency",
      "effectiveFrom": "Effective from",
      "effectiveFromHint": "The price applies to runs from this date.",
      "created": "Price added",
      "updated": "Price saved",
      "deleted": "Price deleted",
      "delete": "Delete price",
      "deleteTitle": "Delete this price?",
      "deleteDescription": "The price valid from {{date}} is removed. Costs of new runs fall back to the previous price."
    }
  },
  "skills": {
    "title": "Skills",
    "description": "Reusable instructions agents load when a task calls for them. Write them here, upload SKILL.md files or sync a Git repository.",
    "tabs": {
      "skills": "Library",
      "presets": "Presets"
    },
    "new": "New skill",
    "newTitle": "New skill",
    "formDescription": "Describe when to use the skill and write the instructions in Markdown.",
    "create": "Create skill",
    "import": "Import",
    "importFile": "From Markdown file…",
    "importGit": "From GitHub repository…",
    "imported": "Skill “{{name}}” imported",
    "search": "Search skills…",
    "noDescription": "No description",
    "manual": "Written here",
    "disabled": "Disabled",
    "chars": "{{n}} chars",
    "toggle": "Enable {{name}}",
    "download": "Download .md",
    "emptyBody": "This skill has no instructions yet.",
    "fromGit": "Synced from {{repo}} ({{branch}}) · {{path}}. Re-importing overwrites local edits.",
    "created": "Skill “{{name}}” created",
    "updated": "Skill “{{name}}” saved",
    "deleted": "Skill “{{name}}” deleted",
    "delete": "Delete skill",
    "deleteTitle": "Delete “{{name}}”?",
    "deleteDescription": "The skill is removed from the library.",
    "deleteInUse_one": "{{count}} agent uses this skill; it will no longer be able to load it.",
    "deleteInUse_other": "{{count}} agents use this skill; they will no longer be able to load it.",
    "empty": {
      "title": "No skills yet",
      "description": "Skills keep long instructions out of the prompt until an agent needs them."
    },
    "columns": {
      "skill": "Skill",
      "source": "Source",
      "agents": "Agents",
      "size": "Size",
      "updated": "Updated",
      "enabled": "Enabled"
    },
    "form": {
      "name": "Name",
      "slug": "Slug",
      "slugPlaceholder": "generated-from-name",
      "slugHint": "Stable identifier the agent uses to load the skill.",
      "description": "When to use",
      "descriptionHint": "Agents see this line to decide whether to load the skill. Be specific.",
      "enabled": "Enabled",
      "enabledHint": "Disabled skills are hidden from every agent.",
      "body": "Instructions",
      "bodyHint": "Markdown. Steps, rules, examples and templates.",
      "bodyPlaceholder": "# Steps\n1. …"
    },
    "git": {
      "title": "Import from GitHub",
      "description": "Every SKILL.md in the repository becomes a skill. Skills with the same name are updated.",
      "repo": "Repository URL",
      "urlInvalid": "Use a URL like https://github.com/owner/repo",
      "branch": "Branch",
      "path": "Folder",
      "pathHint": "Only import below this path.",
      "disableMissing": "Disable skills that were removed",
      "disableMissingHint": "Skills from this repository and branch that no longer exist are disabled.",
      "submit": "Import",
      "again": "Import again",
      "close": "Close",
      "done_one": "{{count}} skill imported",
      "done_other": "{{count}} skills imported",
      "nothing": "No SKILL.md files found.",
      "imported_one": "{{count}} imported",
      "imported_other": "{{count}} imported",
      "disabled_one": "{{count}} disabled",
      "disabled_other": "{{count}} disabled",
      "errors_one": "{{count}} failed",
      "errors_other": "{{count}} failed"
    },
    "presets": {
      "new": "New preset",
      "newTitle": "New preset",
      "editTitle": "Edit {{name}}",
      "dialogDescription": "A preset is a named set of skills you can add to an agent in one go.",
      "name": "Name",
      "descriptionField": "Description",
      "skills": "Skills",
      "selected_one": "{{count}} skill selected",
      "selected_other": "{{count}} skills selected",
      "noSkills": "No skills",
      "created": "Preset “{{name}}” created",
      "updated": "Preset “{{name}}” saved",
      "deleted": "Preset “{{name}}” deleted",
      "delete": "Delete preset",
      "deleteTitle": "Delete preset “{{name}}”?",
      "deleteDescription": "Agents keep the skills they already have.",
      "empty": {
        "title": "No presets yet",
        "description": "Bundle skills that belong together, such as “Support basics”, and apply them to agents at once."
      }
    }
  },
  "mcp": {
    "title": "MCP integrations",
    "description": "Tools for your agents over the Model Context Protocol, plus the platform's built-in toolsets.",
    "new": "Add integration",
    "newTitle": "Add an integration",
    "formDescription": "Connect a remote MCP server over HTTP. Authenticate with headers or OAuth.",
    "create": "Add integration",
    "search": "Search integrations…",
    "noDescription": "No description",
    "builtin": "Built-in",
    "system": "System",
    "disabled": "Disabled",
    "enable": "Enable",
    "disable": "Disable",
    "lockedBuiltin": "Built-in toolsets run inside the platform. You can rename them, limit their tools and add instructions.",
    "lockedSystem": "This integration is managed by the system; its connection settings can't be changed.",
    "stdioAdminOnly": "Command-line (stdio) integrations run processes on the server and can only be edited by platform admins.",
    "created": "Integration “{{name}}” added",
    "updated": "Integration “{{name}}” saved",
    "deleted": "Integration “{{name}}” deleted",
    "delete": "Delete integration",
    "deleteTitle": "Delete “{{name}}”?",
    "deleteDescription": "Stored credentials and tokens are removed.",
    "deleteInUse_one": "{{count}} agent uses this integration and will lose its tools.",
    "deleteInUse_other": "{{count}} agents use this integration and will lose its tools.",
    "toolCount_one": "{{count}} tool",
    "toolCount_other": "{{count}} tools",
    "empty": {
      "title": "No integrations yet",
      "description": "Add an MCP server to give agents tools such as search, tickets, files or calendars."
    },
    "columns": {
      "integration": "Integration",
      "type": "Type",
      "auth": "Authentication",
      "status": "Status",
      "tools": "Tools",
      "agents": "Agents"
    },
    "status": {
      "enabled": "Enabled",
      "disabled": "Disabled",
      "error": "Error"
    },
    "tabs": {
      "connection": "Connection",
      "tools": "Tools",
      "instructions": "Instructions"
    },
    "transport": {
      "http": "HTTP (Streamable / SSE)",
      "httpHint": "A remote MCP server reachable over HTTPS.",
      "stdio": "Command line (stdio)",
      "stdioHint": "Starts a local process on the server.",
      "stdioAdminOnly": "Platform admins only."
    },
    "auth": {
      "none": "None",
      "headers": "Static headers",
      "oauth": "OAuth 2.0"
    },
    "form": {
      "name": "Name",
      "enabled": "Enabled",
      "description": "Description",
      "transport": "Transport",
      "url": "Server URL",
      "command": "Command",
      "args": "Arguments",
      "argsHint": "One argument per line.",
      "env": "Environment variables",
      "envHint": "Passed to the process. Values are write-only.",
      "auth": "Authentication",
      "headers": "Headers",
      "headersHint": "For example Authorization: Bearer …. Values are encrypted and write-only.",
      "prefix": "Tool prefix",
      "prefixHint": "Prepended to tool names (prefix__tool) to avoid clashes between integrations.",
      "prefixInvalid": "Use letters, digits and underscores only.",
      "instructions": "Instructions for agents",
      "instructionsHint": "Added to the system prompt of agents using this integration: when to use which tool, conventions, limits."
    },
    "oauth": {
      "title": "OAuth 2.0",
      "needed": "Authorize",
      "expired": "Token expired",
      "expires": "Expires",
      "status": {
        "authorized": "Authorized",
        "expired": "Expired",
        "not_configured": "Not authorized"
      },
      "discover": "Discover endpoints",
      "discovered": "OAuth endpoints filled in",
      "clientId": "Client ID",
      "clientIdHint": "Leave empty to register automatically (dynamic client registration).",
      "clientSecret": "Client secret",
      "secretKeep": "Keep current secret",
      "authorizationUrl": "Authorization URL",
      "tokenUrl": "Token URL",
      "registrationUrl": "Registration URL",
      "resource": "Resource (RFC 8707)",
      "scopes": "Scopes",
      "scopesHint": "Space-separated.",
      "saveFirst": "Save the integration, then authorize it.",
      "saveChangesFirst": "Save your changes before authorizing.",
      "authorizeHint": "Sign in with the service in a pop-up window to grant access.",
      "waiting": "Waiting for you to finish in the pop-up…",
      "authorize": "Authorize",
      "reauthorize": "Authorize again",
      "revoke": "Revoke",
      "revoked": "Access for “{{name}}” revoked",
      "success": "“{{name}}” is authorized",
      "error": "Authorization failed",
      "discoverFailed": "Couldn't discover OAuth endpoints"
    },
    "test": {
      "action": "Test connection",
      "title": "Connection test",
      "hint": "Connects to the server and lists its tools.",
      "running": "Connecting…",
      "cached_one": "{{count}} tool cached from the last test.",
      "cached_other": "{{count}} tools cached from the last test.",
      "ok_one": "Connected in {{ms}} ms · {{count}} tool",
      "ok_other": "Connected in {{ms}} ms · {{count}} tools",
      "okNamed_one": "{{name}}: connected in {{ms}} ms, {{count}} tool",
      "okNamed_other": "{{name}}: connected in {{ms}} ms, {{count}} tools",
      "failed": "The connection failed.",
      "failedNamed": "Couldn't connect to {{name}}",
      "needsAuth": "The server requires authorization. Authorize it in the connection settings.",
      "online": "Connected",
      "offline": "Failed",
      "seeTools": "Choose which tools agents may use under"
    },
    "tools": {
      "all": "All tools",
      "allHint": "New tools on the server become available automatically.",
      "selected": "Only selected tools",
      "selectedHint": "Agents can only use the tools you tick.",
      "countAll_one": "{{count}} tool",
      "countAll_other": "{{count}} tools",
      "readOnly": "Read-only",
      "destructive": "Destructive",
      "unavailable": "Test the connection to load the tool list."
    }
  },
  "budgets": {
    "title": "Budgets",
    "description": "Cap what the organization or a single agent may spend per day, week or month.",
    "new": "New policy",
    "newTitle": "New budget policy",
    "editTitle": "Edit budget policy",
    "dialogDescription": "Spend is the cost of runs in the current period, from the model prices.",
    "statusTitle": "This period",
    "statusDescription": "Spend so far against each cap.",
    "policiesTitle": "Policies",
    "alertsTitle": "Alerts",
    "alertsDescription": "Raised when spend crosses a policy's warning threshold.",
    "noAlerts": "No alerts so far",
    "unnamed": "Policy #{{id}}",
    "ofLimit": "of {{limit}}",
    "resets": "resets {{when}}",
    "created": "Budget policy created",
    "updated": "Budget policy saved",
    "deleted": "Budget policy deleted",
    "delete": "Delete policy",
    "deleteTitle": "Delete this budget policy?",
    "deleteDescription": "Spend is no longer capped by this policy. Past alerts are removed.",
    "empty": {
      "title": "No budgets yet",
      "description": "Without a policy there is no limit on spend. Add one per organization or per agent."
    },
    "columns": {
      "policy": "Policy",
      "period": "Period",
      "limit": "Limit",
      "threshold": "Warn at",
      "action": "When exceeded",
      "enabled": "Active"
    },
    "scope": {
      "tenant": "Whole organization",
      "agent": "Single agent"
    },
    "period": {
      "day": "Daily",
      "week": "Weekly",
      "month": "Monthly"
    },
    "action": {
      "block": "Block runs",
      "blockHint": "New runs are refused once the cap is reached.",
      "warn": "Warn only",
      "warnHint": "Runs continue; an alert is recorded."
    },
    "level": {
      "ok": "On track",
      "warning": "Near limit",
      "exceeded": "Over limit",
      "blocked": "Blocking",
      "off": "Paused"
    },
    "form": {
      "name": "Name",
      "namePlaceholder": "e.g. Monthly cap",
      "scope": "Applies to",
      "agent": "Agent",
      "period": "Period",
      "limit": "Spend cap",
      "limitHint": "In the currency of your model prices.",
      "threshold": "Warn at",
      "thresholdHint": "Percentage of the cap that raises an alert.",
      "action": "When the cap is reached",
      "enabled": "Policy active"
    }
  },
  "copy": {
    "action": "Copy configuration",
    "rowAction": "Copy Studio configuration here…",
    "title": "Copy configuration",
    "description": "Copy providers, models, skills, integrations and agents to another organization.",
    "from": "From",
    "to": "To",
    "what": "What to copy",
    "note": "Dependencies come along automatically (an agent brings its model and provider). Items that already exist in the target by name are reused, never overwritten. API keys are copied; OAuth authorizations are not.",
    "noTargets": "You need to be an owner or admin of another organization.",
    "submit": "Copy",
    "close": "Done",
    "done": "Copied from {{source}} to {{target}}.",
    "nothing": "Nothing to copy.",
    "collection": "Type",
    "created": "Created",
    "reused": "Already there",
    "collections": {
      "model_providers": "Model providers",
      "models": "Models & prices",
      "skills": "Skills",
      "mcp_servers": "MCP integrations",
      "agents": "Agents",
      "agent_automations": "Agent automations"
    },
    "hints": {
      "model_providers": "Endpoints, API keys and headers.",
      "models": "Models with limits, fallbacks and price history.",
      "skills": "The skill library.",
      "mcp_servers": "Integration settings and credentials, without OAuth tokens.",
      "agents": "Agents with their tags, skills and integrations.",
      "agent_automations": "Automations with their parameters."
    }
  },
  "connectors": {
    "title": "Connectors",
    "description": "Bring agents into chat platforms. Each connector signs in as a bot account and lets one agent answer its messages.",
    "new": "New connector",
    "search": "Search connectors…",
    "columns": {
      "connector": "Connector",
      "agent": "Agent",
      "status": "Status",
      "enabled": "Enabled",
      "updated": "Updated"
    },
    "noAgent": "No agent",
    "state": {
      "unavailable": "Not available",
      "disabled": "Disabled",
      "noAgent": "No agent",
      "connected": "Connected",
      "starting": "Connecting",
      "error": "Error",
      "stopped": "Not running"
    },
    "stateHint": {
      "unavailable": "{{type}} connectors are not available yet.",
      "disabled": "Turned off. Enable it to start listening for messages.",
      "noAgent": "Assign an agent to start this connector.",
      "stopped": "Enabled, but not running yet. It starts within a few seconds."
    },
    "statusSince": "since",
    "enableBlocked": "Assign an agent before enabling",
    "actions": {
      "test": "Test connection",
      "enable": "Enable",
      "disable": "Disable",
      "delete": "Delete",
      "conversations": "Conversations"
    },
    "delete": {
      "title": "Delete {{name}}?",
      "description": "The bot stops answering immediately. The connector's conversation memory and uploaded files are deleted too. Run history stays in Insights.",
      "confirm": "Delete connector"
    },
    "toast": {
      "created": "Connector {{name}} created",
      "updated": "Connector {{name}} saved",
      "deleted": "Connector {{name}} deleted",
      "enabled": "{{name}} enabled",
      "disabled": "{{name}} disabled"
    },
    "empty": {
      "title": "No connectors yet",
      "description": "Connect a bot account on a chat platform so people can talk to an agent where they already work."
    },
    "typePicker": {
      "title": "Choose a platform",
      "description": "What should the agent connect to?",
      "comingSoon": "Coming soon",
      "fields_one": "{{count}} setting",
      "fields_other": "{{count}} settings"
    },
    "form": {
      "newTitle": "New {{type}} connector",
      "newDescription": "Connect a {{type}} bot account to an agent.",
      "sections": {
        "general": "General",
        "agent": "Agent & tools",
        "connection": "Connection"
      },
      "name": "Name",
      "enabled": "Enabled",
      "enabledHint": "Listen for messages and answer as the bot.",
      "agent": "Answering agent",
      "agentHint": "Answers every message the bot receives.",
      "agentPlaceholder": "Select an agent…",
      "agentArchived": "{{name}} (archived)",
      "agentRequired": "Assign an agent before enabling the connector.",
      "mcp": "Extra integrations",
      "mcpHint": "Available to the agent in this connector, on top of its own integrations.",
      "mcpEmpty": "No integrations set up yet.",
      "mcpSearch": "Search integrations…",
      "mcpSelected_one": "{{count}} selected",
      "mcpSelected_other": "{{count}} selected",
      "secretStored": "Stored",
      "secretKeep": "Leave blank to keep the stored value",
      "secretStoredHint": "A value is stored and never shown. Enter a new one to replace it.",
      "numberInvalid": "Enter a number of 0 or more.",
      "unavailable": "{{type}} connectors are not available yet. You can save the configuration, but the connector will not run.",
      "runtime": "Runtime status",
      "secretClear": "Clear",
      "secretClearLabel": "Clear the stored {{field}}",
      "secretCleared": "Will be removed",
      "secretClearedHint": "The stored value is removed when you save.",
      "secretUndo": "Undo"
    },
    "test": {
      "title": "Connection test",
      "hint": "Checks the configuration and signs in with the bot token.",
      "saveFirst": "Save your changes first; the test uses the stored configuration.",
      "action": "Test connection",
      "running": "Testing…",
      "ok": "Signed in as {{name}}",
      "okNoName": "Credentials accepted",
      "failed": "The connection test failed",
      "online": "OK",
      "offline": "Failed",
      "botId": "Bot user ID",
      "okNamed": "{{name}}: connection works",
      "failedNamed": "{{name}}: connection test failed"
    },
    "fields": {
      "mattermost": {
        "websocket_url": {
          "label": "WebSocket URL",
          "help": "WebSocket endpoint of the Mattermost server. The REST API base URL is derived from it."
        },
        "bot_token": {
          "label": "Bot token",
          "help": "Personal access token of the bot account."
        },
        "bot_user_id": {
          "label": "Bot user ID",
          "help": "Optional; detected automatically from the token."
        },
        "thread_memory_hours": {
          "label": "Thread memory (hours)",
          "help": "How long a thread's conversation memory is kept after the last message. 0 = never expire."
        },
        "error_message": {
          "label": "Error reply",
          "help": "Reply posted when the agent fails to answer."
        }
      },
      "slack": {
        "bot_token": {
          "label": "Bot token"
        },
        "app_token": {
          "label": "App token"
        },
        "signing_secret": {
          "label": "Signing secret"
        }
      },
      "telegram": {
        "bot_token": {
          "label": "Bot token"
        }
      }
    },
    "conversations": {
      "title": "Conversations",
      "description": "Threads the bot remembers. Memory expires after the configured idle time.",
      "back": "All connectors",
      "search": "Search conversations…",
      "empty": {
        "title": "No conversations yet",
        "description": "Threads appear here once people message the bot."
      },
      "select": {
        "title": "Select a conversation",
        "description": "Pick a thread to read what was said."
      },
      "thread": "Thread {{id}}",
      "inChannel": "in {{channel}}",
      "turns_one": "{{count}} turn",
      "turns_other": "{{count}} turns",
      "messages_one": "{{count}} stored message",
      "messages_other": "{{count}} stored messages",
      "started": "Started",
      "lastActive": "Last active",
      "threadId": "Thread ID",
      "copyId": "Copy thread ID",
      "unknownSender": "Unknown user",
      "forget": "Forget",
      "forgetTitle": "Forget this conversation?",
      "forgetDescription": "The bot loses its memory of this thread; the next message starts fresh. Run history stays available in Insights.",
      "forgotten": "Conversation memory cleared",
      "viewRuns": "View runs",
      "openRun": "Open run",
      "loadOlder": "Load earlier messages",
      "showPrompt": "Show full prompt",
      "hidePrompt": "Hide full prompt",
      "noTranscript": "No messages recorded for this thread yet.",
      "working": "Working…",
      "notPosted": "Not posted to the chat ({{reason}}).",
      "noReply": "No reply was sent.",
      "toolCalls_one": "{{count}} tool call",
      "toolCalls_other": "{{count}} tool calls",
      "backToList": "Conversations",
      "attachments": "Attachments",
      "attachmentsSkipped": "Not stored: {{files}}",
      "live": "Live",
      "liveHint": "New messages appear as they arrive."
    }
  },
  "schedules": {
    "title": "Schedules",
    "description": "Run automations on a timetable — weekly at fixed times or with a cron expression, in any timezone.",
    "new": "New schedule",
    "newTitle": "New schedule",
    "formDescription": "Choose when the schedule fires and which automations it runs.",
    "create": "Create schedule",
    "created": "Schedule “{{name}}” created",
    "saved": "Schedule “{{name}}” saved",
    "enabledToast": "Schedule “{{name}}” enabled",
    "disabledToast": "Schedule “{{name}}” paused",
    "deleted": "Schedule “{{name}}” deleted",
    "runStarted_one": "“{{name}}” started: {{count}} action queued",
    "runStarted_other": "“{{name}}” started: {{count}} actions queued",
    "search": "Search schedules…",
    "noDescription": "No description",
    "missingTarget": "Deleted automation #{{id}}",
    "moreActions_one": "+{{count}} more",
    "moreActions_other": "+{{count}} more",
    "notPlanned": "Not planned",
    "paused": "Paused",
    "pausedHint": "Paused — only runs when started manually.",
    "lastOk": "Last run succeeded",
    "lastFailed": "Last run failed",
    "toggle": "Enable {{name}}",
    "runNow": "Run now",
    "history": "Execution history",
    "nextRun": "Next run",
    "viewRun": "View run",
    "viewRoom": "View room",
    "toggleDetails": "Show details",
    "delete": "Delete schedule",
    "deleteTitle": "Delete “{{name}}”?",
    "deleteDescription": "The schedule and its execution history are removed. The automations themselves are kept.",
    "columns": {
      "schedule": "Schedule",
      "timing": "When",
      "actions": "Runs",
      "next": "Next run",
      "last": "Last run",
      "enabled": "Enabled"
    },
    "empty": {
      "title": "No schedules yet",
      "description": "Schedules run automations automatically, for example a daily report at 08:00 on weekdays."
    },
    "historyEmpty": {
      "title": "No executions yet",
      "description": "Runs appear here once the schedule fires or is started manually."
    },
    "execStatus": {
      "queued": "Queued",
      "running": "Running",
      "succeeded": "Succeeded",
      "failed": "Failed"
    },
    "trigger": {
      "manual": "Started manually",
      "schedule": "Scheduled"
    },
    "types": {
      "agent_automation": "Agent automation",
      "team_automation": "Team automation"
    },
    "describe": {
      "everyMinute": "Every minute",
      "everyNMinutes_one": "Every minute",
      "everyNMinutes_other": "Every {{count}} minutes",
      "hourly": "Every hour at :{{minute}}",
      "everyNHours_one": "Every hour at :{{minute}}",
      "everyNHours_other": "Every {{count}} hours at :{{minute}}",
      "at": "At {{times}}",
      "everyDay": "every day",
      "weekdays": "on weekdays",
      "weekends": "on weekends",
      "onDays": "on {{days}}",
      "monthDays_one": "on day {{days}} of the month",
      "monthDays_other": "on days {{days}} of the month",
      "inMonths": "in {{months}}",
      "custom": "Custom cron expression",
      "never": "No times set"
    },
    "form": {
      "name": "Name",
      "namePlaceholder": "Weekly sales report",
      "description": "Description",
      "enabled": "Enabled",
      "enabledHint": "Paused schedules keep their settings and can still be run manually.",
      "when": "When",
      "whenHint": "Pick days and times, or write a cron expression for anything more specific.",
      "modeWeekly": "Days & times",
      "modeCron": "Cron",
      "days": "Days",
      "daysRequired": "Pick at least one day",
      "presetWeekdays": "Weekdays",
      "presetEveryDay": "Every day",
      "times": "Times",
      "timesHint": "Local times in the schedule's timezone.",
      "timesRequired": "Add at least one time",
      "timeInvalid": "Use HH:MM (24-hour)",
      "timeN": "Time {{n}}",
      "removeTime": "Remove {{time}}",
      "addTime": "Add time",
      "cron": "Cron expression",
      "cronHint": "minute · hour · day of month · month · day of week — at most once per minute.",
      "presets": "Presets",
      "timezone": "Timezone",
      "timezoneHint": "Leave on the organization default to follow the organization's settings.",
      "orgTimezone": "Organization default",
      "preview": "Preview",
      "previewIncomplete": "Pick at least one day and time",
      "nextRuns": "Next runs ({{tz}})",
      "timezoneIs": "Times are in {{tz}}.",
      "actions": "Actions",
      "actionsHint": "Automations to start each time the schedule fires, in this order.",
      "addAction": "Add action",
      "actionsEmpty": "Add the first automation to run",
      "noTargets": "Create an automation first — schedules run agent or team automations.",
      "actionsRequired": "Add at least one action",
      "target": "Automation",
      "targetPlaceholder": "Choose an automation…",
      "removeAction": "Remove action {{n}}",
      "fileParams": "Has file parameters — cannot be scheduled",
      "noParams": "This automation has no parameters.",
      "paramsMissing": "Fill in the required parameters: {{names}}"
    }
  },
  "evals": {
    "title": "Evaluations",
    "description": "Test agents against fixed prompts with rule checks and an LLM judge, track the pass rate over time and catch regressions.",
    "search": "Search suites…",
    "newSuite": "New suite",
    "editSuite": "Edit suite",
    "createSuite": "Create suite",
    "suiteFormDescription": "A suite is a set of test cases run against one agent.",
    "suiteCreated": "Suite “{{name}}” created",
    "suiteSaved": "Suite “{{name}}” saved",
    "suiteDeleted": "Suite “{{name}}” deleted",
    "suiteActions": "Suite actions",
    "openSuite": "Open suite",
    "deleteSuite": "Delete suite",
    "deleteSuiteTitle": "Delete “{{name}}”?",
    "deleteSuiteDescription": "All cases, runs and results of this suite are removed.",
    "backToSuites": "Back to suites",
    "noDescription": "No description",
    "notRunYet": "Not run yet",
    "needCases": "Add a case first",
    "runSuite": "Run suite",
    "runStarted": "Run #{{id}} started",
    "rerunStarted": "Rerun #{{id}} started",
    "runDeleted": "Run #{{id}} deleted",
    "caseCreated": "Case added",
    "caseSaved": "Case saved",
    "caseDeleted": "Case deleted",
    "caseN": "Case #{{id}}",
    "runN": "Run #{{id}}",
    "rerunOf": "Rerun of #{{id}}",
    "seconds": "s",
    "fields": {
      "name": "Name",
      "namePlaceholder": "Support agent smoke tests",
      "agent": "Agent",
      "agentHint": "Runs always use the agent's current configuration.",
      "agentPlaceholder": "Choose an agent…",
      "description": "Description"
    },
    "columns": {
      "suite": "Suite",
      "agent": "Agent",
      "cases": "Cases",
      "passRate": "Pass rate",
      "lastRun": "Last run",
      "case": "Case",
      "checks": "Checks",
      "tags": "Tags",
      "timeout": "Timeout",
      "run": "Run",
      "status": "Status",
      "results": "Results",
      "cost": "Cost",
      "duration": "Duration",
      "started": "Started"
    },
    "empty": {
      "title": "No eval suites yet",
      "description": "Create a suite, add a few typical prompts with the answers you expect and run it after every change to an agent."
    },
    "tabs": {
      "cases": "Cases ({{count}})",
      "runs": "Runs"
    },
    "searchCases": "Search cases…",
    "newCase": "New case",
    "addCase": "Add case",
    "caseFormDescription": "The prompt is sent to the agent; the output must pass every check you configure.",
    "deleteCase": "Delete case",
    "deleteCaseTitle": "Delete “{{name}}”?",
    "deleteCaseDescription": "Results of earlier runs for this case are removed too.",
    "casesEmpty": {
      "title": "No cases yet",
      "description": "Add test prompts, or start from a template such as “stays on topic” or “refuses harmful requests”."
    },
    "presetLabel": "Start from a template",
    "presetHint": "Fills in a prompt and checks you can adapt.",
    "presetPlaceholder": "Choose a template…",
    "presets": {
      "cleanRun": {
        "name": "Smoke test",
        "description": "Answers without errors"
      },
      "jsonOutput": {
        "name": "JSON output",
        "description": "Returns a bare JSON object"
      },
      "noSystemLeak": {
        "name": "No prompt leak",
        "description": "Does not reveal its system prompt"
      },
      "politeRefusal": {
        "name": "Refuses harmful requests",
        "description": "Declines clearly and politely"
      },
      "staysOnTopic": {
        "name": "Stays on topic",
        "description": "Answers the question without drifting"
      },
      "conciseHelpful": {
        "name": "Concise and helpful",
        "description": "Actionable answer without filler"
      },
      "unknownHonest": {
        "name": "Admits what it doesn't know",
        "description": "Does not invent facts"
      },
      "groundedAnswer": {
        "name": "Grounded in knowledge",
        "description": "Summarises real sources instead of guessing"
      }
    },
    "caseFields": {
      "name": "Name",
      "namePlaceholder": "Opening hours question",
      "input": "Prompt",
      "inputHint": "Sent to the agent as the user message.",
      "assertions": "Checks",
      "assertionsHint": "All configured checks must pass. Text checks ignore case.",
      "contains": "Must contain",
      "notContains": "Must not contain",
      "onePerLine": "One phrase per line.",
      "regex": "Must match regular expression",
      "regexHint": "Rust regex syntax; use (?i) for case-insensitive matching.",
      "judge": "LLM judge instructions",
      "judgeHint": "Optional. The organization's judge model scores the answer from 0 to 1.",
      "judgePlaceholder": "Pass if the answer is friendly and mentions the opening hours.",
      "threshold": "Judge pass threshold",
      "thresholdHint": "Minimum judge score (0–1) to pass.",
      "expectReply": "Reply",
      "expectReplyHint": "Whether a chat connector would post the answer. Silent: the agent must stay quiet (answer with [[no_reply]]).",
      "expectReplyOptions": {
        "any": "Either is fine",
        "reply": "Must reply",
        "silent": "Must stay silent"
      },
      "timeout": "Timeout",
      "tags": "Tags",
      "tagsPlaceholder": "smoke, safety…"
    },
    "checks": {
      "contains_one": "contains {{count}}",
      "contains_other": "contains {{count}}",
      "notContains_one": "excludes {{count}}",
      "notContains_other": "excludes {{count}}",
      "regex": "regex",
      "judge": "judge ≥ {{threshold}}",
      "replies": "replies",
      "silent": "stays silent",
      "toolCalls_one": "expects {{count}} call",
      "toolCalls_other": "expects {{count}} calls",
      "forbiddenCalls_one": "forbids {{count}} call",
      "forbiddenCalls_other": "forbids {{count}} calls",
      "none": "No checks"
    },
    "runStatus": {
      "pending": "Queued",
      "running": "Running",
      "done": "Done",
      "error": "Error"
    },
    "resultStatus": {
      "pass": "Pass",
      "fail": "Fail",
      "error": "Error"
    },
    "passRate": "Pass rate",
    "passOf": "{{pass}} of {{total}} passed",
    "results": "Results",
    "passCount_one": "{{count}} passed",
    "passCount_other": "{{count}} passed",
    "failCount_one": "{{count}} failed",
    "failCount_other": "{{count}} failed",
    "errorCount_one": "{{count}} error",
    "errorCount_other": "{{count}} errors",
    "duration": "Duration",
    "cost": "Cost",
    "trend": "Pass rate over time",
    "waiting": "Waiting for a worker…",
    "progress": "{{done}} of {{total}} cases done",
    "progressLabel": "Run progress",
    "inFlight_one": "{{count}} case running",
    "inFlight_other": "{{count}} cases running",
    "caseRunning": "Running",
    "regressionTitle": "Regression detected",
    "regressionText": "The pass rate dropped from {{from}} to {{to}} ({{points}} points) compared with run #{{id}}.",
    "runFailed": "The run failed",
    "filterAll": "All ({{count}})",
    "filterFailed": "Not passed ({{count}})",
    "compareWithPrevious": "Compare with previous",
    "rerunFailed_one": "Rerun {{count}} failed case",
    "rerunFailed_other": "Rerun {{count}} failed cases",
    "deleteRun": "Delete run",
    "deleteRunTitle": "Delete run #{{id}}?",
    "deleteRunDescription": "Its results are removed; the agent runs stay in the run log.",
    "noResultsYet": "Results appear here as cases finish.",
    "allPassed": "Every case passed.",
    "noResults": "This run has no results.",
    "judgeScore": "judge {{score}}",
    "judge": "Judge",
    "judgeWithScore": "Judge · score {{score}}",
    "input": "Prompt",
    "failureReasons": "Why it failed",
    "output": "Output",
    "replySuppressed": "Not posted to the chat ({{reason}}). Raw output below, for debugging:",
    "reasoning": "Reasoning",
    "replySuppressedNoOutput": "Not posted to the chat ({{reason}}).",
    "suppressReason": {
      "empty": "empty output",
      "sentinel": "no-reply sentinel",
      "reasoning_only": "reasoning only"
    },
    "noOutput": "No output.",
    "viewAgentRun": "View agent run",
    "selectForCompare": "Select for comparison",
    "selectRun": "Select run #{{id}}",
    "selectTwo": "Select two runs to compare them.",
    "twoSelected": "Two runs selected.",
    "compareSelected": "Compare",
    "runsEmpty": {
      "title": "No runs yet",
      "description": "Run the suite to see how the agent does on every case."
    },
    "compare": {
      "title": "Compare runs",
      "description": "How each case changed between two runs.",
      "baseline": "Baseline",
      "candidate": "Compared run",
      "onlyChanged": "Only changed cases",
      "noChanges": "No case changed between these runs.",
      "noCases": "These runs have no results to compare.",
      "notRun": "Not run",
      "counts": {
        "regressed_one": "{{count}} regressed",
        "regressed_other": "{{count}} regressed",
        "fixed_one": "{{count}} fixed",
        "fixed_other": "{{count}} fixed",
        "changed_one": "{{count}} changed",
        "changed_other": "{{count}} changed",
        "added_one": "{{count}} only in compared run",
        "added_other": "{{count}} only in compared run",
        "removed_one": "{{count}} only in baseline",
        "removed_other": "{{count}} only in baseline",
        "same_one": "{{count}} unchanged",
        "same_other": "{{count}} unchanged"
      },
      "change": {
        "regressed": "Regressed",
        "fixed": "Fixed",
        "changed": "Changed",
        "added": "New",
        "removed": "Missing",
        "same": "Unchanged"
      }
    },
    "alerts": {
      "title": "Regressions",
      "drop": "−{{points}} pts"
    },
    "mcp": {
      "section": "Integrations and tool calls",
      "sectionHint": "Eval runs never contact the agent's integrations: every tool is mocked. Check which tools the agent calls.",
      "mocked": "Integrations mocked",
      "mockedHint": "Eval runs never contact the agent's MCP integrations or built-in toolsets: every tool call is mocked and recorded.",
      "stubs": "Canned responses",
      "stubsHint": "Optional JSON object: exposed tool name → response, to steer the conversation. Other tools answer {\"ok\": true}.",
      "expected": "Expected tool calls",
      "expectedHint": "JSON array of {\"tool\", \"args_contains\"}. Each must be called successfully; args_contains is optional (key by key, values exactly; null = empty or not provided).",
      "forbidden": "Forbidden tool calls",
      "forbiddenHint": "JSON array in the same format. The case fails if the agent attempts any of them.",
      "invalidStubs": "Enter a JSON object, e.g. {\"tool_name\": {\"ok\": true}}.",
      "invalidToolCalls": "Enter a JSON array of {\"tool\": \"…\", \"args_contains\": {…}}.",
      "toolCalls": "Tool calls",
      "missingTitle_one": "{{count}} integration has no cached tool list",
      "missingTitle_other": "{{count}} integrations have no cached tool list",
      "missingText": "{{names}}: eval runs can only mock the tools a case names. Refresh the tool lists to mock all their tools.",
      "refresh": "Refresh tool lists",
      "refreshed": "Tool lists refreshed",
      "refreshedPartly": "Some tool lists could not be refreshed: {{names}}"
    }
  },
  "teams": {
    "title": "Teams",
    "detailTitle": "Team",
    "description": "Groups of agents that work together in workplace rooms, led by one leader.",
    "new": "New team",
    "search": "Search teams…",
    "noDescription": "No description",
    "noMembers": "No members yet",
    "memberCount_one": "{{count}} member",
    "memberCount_other": "{{count}} members",
    "concurrency_one": "Up to {{count}} member works at the same time",
    "concurrency_other": "Up to {{count}} members work at the same time",
    "ready": "Ready",
    "notReady": "Not ready",
    "backToList": "Back to teams",
    "delete": "Delete team",
    "deleteTitle": "Delete “{{name}}”?",
    "deleteDescription": "The team, its members and its automations are removed.",
    "deleteInUse_one": "Its {{count}} room is stopped and keeps its transcript. Members and automations are removed.",
    "deleteInUse_other": "Its {{count}} rooms are stopped and keep their transcripts. Members and automations are removed.",
    "empty": {
      "title": "Assemble your first team",
      "description": "A team combines agents and simple role-players under a leader who delegates the work in a room."
    },
    "tabs": {
      "teams": "Teams",
      "members": "Members",
      "rooms": "Rooms",
      "automations": "Automations"
    },
    "columns": {
      "team": "Team",
      "members": "Members",
      "leader": "Leader",
      "status": "Status",
      "rooms": "Rooms",
      "updated": "Updated"
    },
    "actions": {
      "open": "Open team"
    },
    "toast": {
      "created": "Team “{{name}}” created",
      "saved": "Team “{{name}}” saved",
      "deleted": "Team “{{name}}” deleted",
      "memberAdded": "{{name}} joined the team",
      "memberSaved": "{{name}} updated",
      "memberRemoved": "{{name}} removed from the team",
      "leader": "{{name}} now leads the team"
    },
    "form": {
      "newTitle": "New team",
      "editTitle": "Edit team",
      "description": "Members and automations are managed on the team page.",
      "create": "Create team",
      "name": "Name",
      "namePlaceholder": "Customer support",
      "teamDescription": "Description",
      "teamDescriptionHint": "What the team is for. The AI team builder reads this too.",
      "concurrency": "Parallel members",
      "concurrencyHint": "How many members may work at the same time in a room (1–16)."
    },
    "kinds": {
      "agent": "Agent",
      "simple": "Simple"
    },
    "member": {
      "add": "Add member",
      "addTitle": "Add member",
      "addDescription": "Add an existing agent or a simple role with its own prompt and model.",
      "editDescription": "Changes apply to the team's rooms immediately.",
      "kind": "Type",
      "kindHint": {
        "agent": "Uses an agent from Studio, with its prompt, model, tools and skills.",
        "simple": "A lightweight role: a prompt, a model and a temperature, without tools."
      },
      "agent": "Agent",
      "agentHint": "Prompt, model and tools come from this agent.",
      "agentPlaceholder": "Choose an agent",
      "displayName": "Name in rooms",
      "displayNameAgentHint": "Optional. Leave empty to use the agent's name.",
      "displayNamePlaceholder": "Researcher",
      "rolePrompt": "Role prompt",
      "rolePromptHint": "The system prompt this member works with.",
      "rolePromptPlaceholder": "You research facts and cite your sources…",
      "model": "Model",
      "modelPlaceholder": "Choose a model",
      "temperature": "Temperature",
      "temperatureHint": "Lower is more focused, higher is more creative.",
      "invocation": "When to involve",
      "invocationHint": "Tells the leader when to hand work to this member.",
      "invocationPlaceholder": "Ask for any research or fact checking.",
      "leader": "Team leader",
      "leaderHint": "The leader plans the work and delegates to the others. The current leader steps down.",
      "leaderFirst": "The first member always leads the team.",
      "leaderCurrent": "This member leads the team. Make another member leader to change that."
    },
    "members": {
      "description": "The leader coordinates; the order is the order in which members appear in rooms.",
      "leader": "Leader",
      "linkedAgent": "Agent: {{name}}",
      "missingAgent": "Linked agent is missing",
      "simpleModel": "{{model}} · temperature {{temperature}}",
      "missingModel": "No model selected",
      "makeLeader": "Make leader",
      "up": "Move up",
      "down": "Move down",
      "moveUp": "Move {{name}} up",
      "moveDown": "Move {{name}} down",
      "actions": "Actions for {{name}}",
      "remove": "Remove",
      "removeTitle": "Remove {{name}}?",
      "removeDescription": "The member leaves the team.",
      "removeInRooms_one": "The member leaves the team and stops working in its {{count}} room.",
      "removeInRooms_other": "The member leaves the team and stops working in its {{count}} rooms.",
      "notReady": "This team can't start rooms yet",
      "noLeader": "Mark exactly one member as leader.",
      "multipleLeaders": "More than one member is marked as leader; keep exactly one.",
      "emptyTitle": "No members yet",
      "emptyDescription": "Add agents yourself or let the AI team builder propose a roster."
    },
    "designer": {
      "buildAction": "Build with AI",
      "tweakAction": "Tweak with AI",
      "buildTitle": "AI team builder",
      "tweakTitle": "AI team tweaking",
      "buildDescription": "Describe what “{{team}}” should do. The agent proposes roles and adds them as simple members.",
      "tweakDescription": "Ask for changes to the roster of “{{team}}”: names, roles, leader, models or instructions.",
      "buildEmpty": "Members are added as the agent works; you can edit or remove them afterwards.",
      "tweakEmpty": "Changes are applied as the agent works; the roster behind this panel updates live.",
      "agent": "Agent",
      "agentPlaceholder": "Choose an agent",
      "memberModel": "Model for new members",
      "memberTemperature": "Temperature for new members",
      "message": "Message",
      "buildPlaceholder": "Describe the team you need…",
      "tweakPlaceholder": "Describe the change…",
      "send": "Send",
      "stop": "Stop",
      "reset": "Start over",
      "working": "Working…",
      "failed": "The run failed.",
      "cancelled": "Stopped.",
      "interrupted": "The connection closed before the run finished.",
      "openRun": "Open run",
      "starters": {
        "build1": "Propose a small team (3–4 members) for this purpose, with a clear leader.",
        "build2": "Add a critical reviewer who checks the others' work before it is final.",
        "tweak1": "Review the roster and sharpen each member's instructions.",
        "tweak2": "Make the leader delegate more and write less itself."
      }
    },
    "phases": {
      "agent_running": "Working",
      "awaiting_user_input": "Waiting for input",
      "idle_active": "Idle",
      "turn_budget_exhausted": "Budget used up",
      "paused": "Paused"
    },
    "rooms": {
      "stats": {
        "rooms": "Rooms",
        "active": "Active rooms",
        "messages": "Messages",
        "lastActivity": "Last activity"
      },
      "contributors": "Messages per member",
      "search": "Search rooms…",
      "columns": {
        "room": "Room",
        "phase": "State",
        "messages": "Messages",
        "members": "Members",
        "lastMessage": "Last message"
      },
      "emptyTitle": "No rooms yet",
      "emptyDescription": "Rooms started with this team in the workplace, by hand or from an automation, appear here."
    },
    "automations": {
      "title": "Team automations",
      "newTitle": "New team automation",
      "create": "Create automation",
      "formDescription": "A reusable brief that starts a room with the team, from here, a schedule or its webhook.",
      "team": "Team",
      "teamPlaceholder": "Choose a team",
      "emptyTitle": "No team automations",
      "emptyDescription": "Turn a recurring request into a brief with parameters that opens a room with the team.",
      "deleteDescription": "Schedules and integrations that start this automation will stop working.",
      "needsWorkplace": "Running automations requires workplace access.",
      "apiDescription": "Let other systems start a room with an HTTP request.",
      "runDescription": "Fill in the parameters. A new room with {{team}} opens and receives the brief.",
      "startRoom": "Start room",
      "started": "Room started",
      "startedHint": "The team is working on the brief; open the room in the workplace to follow along.",
      "columns": {
        "automation": "Automation",
        "team": "Team",
        "setup": "Setup",
        "runs": "Runs"
      },
      "brief": {
        "title": "Brief",
        "description": "What the room starts with. Use parameters as placeholders.",
        "roomTitle": "Room title",
        "roomTitleHint": "Optional. Defaults to the automation's name.",
        "roomTitlePlaceholder": "Research: {{topic}}",
        "message": "Opening message",
        "messageHint": "Posted to the room as the first message; the leader picks it up.",
        "instructions": "Room instructions",
        "instructionsHint": "Extra instructions every member of the room sees."
      },
      "room": {
        "title": "Room settings",
        "description": "Settings for rooms this automation starts. Empty fields use the usual room defaults.",
        "defaults": "Room defaults",
        "overrides_one": "{{count}} setting",
        "overrides_other": "{{count}} settings",
        "contextMax": "Context messages",
        "contextMaxHint": "Recent messages members see (10–500, default 100).",
        "turnBudget": "Turn budget",
        "turnBudgetHint": "Maximum member turns before the room stops.",
        "noLimit": "No limit",
        "compression": "Compress older messages",
        "compressionHint": "Summarise messages beyond the context window.",
        "compressionKb": "Store summaries in the knowledge base",
        "compressionKbHint": "Lets members search earlier parts of the conversation.",
        "hitl": "Human in the loop",
        "hitlHint": "The leader can pause and ask a person before continuing.",
        "hitlCondition": "When to ask",
        "hitlConditionPlaceholder": "Before sending anything to a customer.",
        "guardrails": "Guardrails",
        "guardrailsHint": "One rule per line; every member must follow them.",
        "guardrailsPlaceholder": "Never share personal data.",
        "data": "Room data",
        "dataHint": "Facts members can read, like a customer or project name.",
        "dataAdd": "Add value",
        "sentry": "Sentry organization",
        "sentryHint": "Organization slug for Sentry tools in this room."
      },
      "toast": {
        "created": "Automation “{{name}}” created",
        "saved": "Automation “{{name}}” saved",
        "deleted": "Automation “{{name}}” deleted"
      },
      "history": {
        "open": "Run history",
        "title": "Runs of {{name}}",
        "description": "Rooms this automation started, newest first.",
        "emptyTitle": "No runs yet",
        "emptyDescription": "Run the automation or call its webhook to start a room.",
        "turns_one": "{{formatted}} turn",
        "turns_other": "{{formatted}} turns"
      }
    }
  }
}
